import type { Task } from "@/services/task.service";
import { getStageLabel } from "@/components/tasks/types";

export interface ProdTaskRow {
	id: string;
	title: string;
	projectName: string;
	status: string;
	statusLabel: string;
}

export interface UserGroup {
	userId: string | null;
	name: string;
	email: string;
	tasks: ProdTaskRow[];
}

const UNASSIGNED_KEY = "__unassigned__";

/**
 * Filter tasks by selected projects/users, then group by assigned user.
 */
export function buildProdReport(
	tasks: Task[],
	projectsById: Map<string, string>,
	selectedProjectIds: string[],
	selectedUserIds: string[],
): UserGroup[] {
	const projectSet = new Set(selectedProjectIds);
	const userSet = new Set(selectedUserIds);

	const filtered = tasks.filter((t) => {
		if (projectSet.size && !projectSet.has(t.project_id)) return false;
		if (userSet.size && !userSet.has(t.assigned_to?.id ?? "")) return false;
		return true;
	});

	const users = new Map<string, UserGroup>();

	for (const t of filtered) {
		const userKey = t.assigned_to?.id ?? UNASSIGNED_KEY;
		let userGroup = users.get(userKey);
		if (!userGroup) {
			userGroup = {
				userId: t.assigned_to?.id ?? null,
				name: t.assigned_to?.full_name ?? "Unassigned",
				email: t.assigned_to?.email ?? "",
				tasks: [],
			};
			users.set(userKey, userGroup);
		}

		userGroup.tasks.push({
			id: t.id,
			title: t.title,
			projectName: projectsById.get(t.project_id) ?? "Unknown project",
			status: t.status,
			statusLabel: getStageLabel(t.status),
		});
	}

	return Array.from(users.values())
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((u) => ({
			...u,
			tasks: u.tasks.sort((a, b) => a.title.localeCompare(b.title)),
		}));
}
